'use client';

import { Tabs as TabsPrimitive } from '@base-ui/react/tabs';
import { cn } from '@zealot/ui/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

type TabsProps = Omit<TabsPrimitive.Root.Props, 'onValueChange'> & {
  onValueChange?: (value: string) => void;
};

const Tabs = ({ className, orientation = 'horizontal', onValueChange, ...props }: TabsProps) => {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      orientation={orientation}
      data-orientation={orientation}
      onValueChange={onValueChange ? (value) => onValueChange(value as string) : undefined}
      className={cn('group/tabs flex gap-2 data-horizontal:flex-col', className)}
      {...props}
    />
  );
};

// gloss
const tabsListVariants = cva(
  'group/tabs-list inline-flex w-fit items-center justify-center text-muted-foreground group-data-vertical/tabs:h-fit group-data-vertical/tabs:flex-col',
  {
    variants: {
      size: {
        sm: 'group-data-horizontal/tabs:h-8 p-[3px]',
        default: 'group-data-horizontal/tabs:h-9 p-1',
        lg: 'group-data-horizontal/tabs:h-10 p-1',
      },
      variant: {
        default: 'rounded-lg bg-muted',
        line: 'gap-3 rounded-none border-b border-border bg-transparent',
        card: 'gap-2 rounded-none border-b border-border bg-transparent group-data-horizontal/tabs:h-auto',
        soft: 'gap-2 bg-transparent p-0 group-data-horizontal/tabs:h-auto',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

type TabsListContextValue = {
  variant: NonNullable<VariantProps<typeof tabsListVariants>['variant']>;
};

const TabsListContext = React.createContext<TabsListContextValue>({ variant: 'default' });

type TabsListProps = TabsPrimitive.List.Props &
  VariantProps<typeof tabsListVariants> & {
    className?: string;
  };

const TabsList = ({ className, variant = 'default', size = 'default', ...props }: TabsListProps) => {
  return (
    <TabsListContext value={{ variant: variant ?? 'default' }}>
      <TabsPrimitive.List
        data-slot="tabs-list"
        data-variant={variant}
        className={cn(tabsListVariants({ variant, size }), className)}
        {...props}
      />
    </TabsListContext>
  );
};

// gloss
const tabsTriggerVariants = cva(
  [
    'relative inline-flex flex-1 items-center justify-center gap-1.5 border font-medium whitespace-nowrap text-foreground/60 transition-all',
    'group-data-vertical/tabs:w-full group-data-vertical/tabs:justify-start',
    'hover:text-foreground',
    'focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-1 focus-visible:outline-ring',
    'disabled:pointer-events-none disabled:opacity-50',
    'aria-disabled:pointer-events-none aria-disabled:opacity-50',
    'dark:text-muted-foreground dark:hover:text-foreground',
    '[&_svg]:pointer-events-none [&_svg]:shrink-0',
  ],
  {
    variants: {
      size: {
        sm: "px-1.5 py-0.5 text-xs [&_svg:not([class*='size-'])]:size-3.5",
        default: "px-3 py-1 text-sm [&_svg:not([class*='size-'])]:size-4",
        lg: "px-4 py-1.5 text-sm [&_svg:not([class*='size-'])]:size-4",
      },
      variant: {
        default:
          'h-[calc(100%-1px)] rounded-md border-transparent data-active:bg-background data-active:text-foreground data-active:shadow-sm dark:data-active:border-input dark:data-active:bg-input/30 dark:data-active:text-foreground',
        line: 'h-[calc(100%-1px)] rounded-none border-transparent bg-transparent data-active:bg-transparent data-active:shadow-none dark:data-active:border-transparent dark:data-active:bg-transparent after:absolute after:bg-foreground after:opacity-0 after:transition-opacity group-data-horizontal/tabs:after:inset-x-0 group-data-horizontal/tabs:after:bottom-[-1px] group-data-horizontal/tabs:after:h-0.5 group-data-vertical/tabs:after:inset-y-0 group-data-vertical/tabs:after:-right-[1px] group-data-vertical/tabs:after:w-0.5 data-active:after:opacity-100',
        card: 'h-auto rounded-t-md rounded-b-none border-border border-b-0 bg-muted px-6 py-2.5 hover:bg-muted/70 data-active:border-t-2 data-active:border-t-primary data-active:border-l-0 data-active:border-r-0 data-active:border-b-0 data-active:bg-background data-active:text-primary',
        soft: 'h-auto flex-none rounded-lg border-transparent hover:bg-muted data-active:bg-primary/10 data-active:text-primary data-active:hover:bg-primary/10 data-active:hover:text-primary',
      },
    },
    defaultVariants: {
      size: 'default',
      variant: 'default',
    },
  },
);

type TabsTriggerProps = TabsPrimitive.Tab.Props &
  VariantProps<typeof tabsTriggerVariants> & {
    className?: string;
  };

const TabsTrigger = ({ className, variant, size = 'default', ...props }: TabsTriggerProps) => {
  const context = React.use(TabsListContext);
  const resolvedVariant = variant ?? context.variant;

  return (
    <TabsPrimitive.Tab
      data-slot="tabs-trigger"
      className={cn(tabsTriggerVariants({ variant: resolvedVariant, size }), className)}
      {...props}
    />
  );
};

const TabsIndicator = ({ className, ...props }: TabsPrimitive.Indicator.Props & { className?: string }) => {
  return (
    <TabsPrimitive.Indicator
      data-slot="tabs-indicator"
      className={cn(
        'absolute bottom-0 h-0.5 bg-foreground transition-all duration-200 ease-out',
        'group-data-vertical/tabs:right-0 group-data-vertical/tabs:bottom-auto group-data-vertical/tabs:h-auto group-data-vertical/tabs:w-0.5',
        className,
      )}
      {...props}
    />
  );
};

const TabsContent = ({ className, ...props }: TabsPrimitive.Panel.Props) => {
  return (
    <TabsPrimitive.Panel data-slot="tabs-content" className={cn('flex-1 text-sm outline-none', className)} {...props} />
  );
};

export { Tabs, TabsContent, TabsIndicator, TabsList, TabsTrigger, tabsListVariants, tabsTriggerVariants };
